import React, { createContext, useContext, useState, useEffect, useCallback } from "react";

const DraftProductsContext = createContext([]);

export const DraftProductsProvider = ({ children }) => {
  const [draftProducts, setDraftProducts] = useState([]);

  // Normalize ID helper - consistent with build-time logic
  const normalizeId = (id) => {
    if (!id) return null;
    return typeof id === "string" && id.includes("/")
      ? id.split("/").pop()
      : id;
  };

  // Load from sessionStorage on mount
  useEffect(() => {
    try {
      const stored = sessionStorage.getItem("draftProducts");
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          setDraftProducts(parsed);
        }
      }
    } catch (e) {
      console.warn("Failed to load draft products from sessionStorage:", e);
    }
  }, []);

  // Save to sessionStorage whenever draftProducts changes
  useEffect(() => {
    try {
      if (Array.isArray(draftProducts) && draftProducts.length > 0) {
        sessionStorage.setItem("draftProducts", JSON.stringify(draftProducts));
      }
    } catch (e) {
      console.warn("Failed to save draft products to sessionStorage:", e);
    }
  }, [draftProducts]);

  // Normalize draft products IDs when setting - memoized to prevent infinite loops
  const setDraftProductsNormalized = useCallback((products) => {
    if (!Array.isArray(products)) {
      setDraftProducts([]);
      return;
    }
    const normalized = products.map((p) => ({
      ...p,
      id: normalizeId(p.id),
    }));
    setDraftProducts(normalized);
  }, []);

  return (
    <DraftProductsContext.Provider
      value={[draftProducts, setDraftProductsNormalized]}
    >
      {children}
    </DraftProductsContext.Provider>
  );
};

export const useDraftProductsContext = () => {
  const context = useContext(DraftProductsContext);
  if (!context) {
    throw new Error(
      "useDraftProductsContext must be used within DraftProductsProvider"
    );
  }
  return context;
};

